/// <amd-module name="Artifacts/Root/hooks/RouteTabManager" />
import * as React from "react";
import { generatePath, matchPath, useHistory, useLocation, useParams } from "react-router-dom";

export interface ITabDefinition<TCompProps = unknown> {
  tabId: string;
  name: string;
  route: string;
  component: React.ComponentType<TCompProps>;
}

/**
 * useRouteTabManager is a hook for managing tabs with browser routing support.
 * To use this hook, parent component should have non-exact route.
 *
 * @param tabDefinitions list of defined tabs
 * @param defaultTab tab that should be rendered during initialization
 */
export function useRouteTabManager<TCompProps>(
  tabDefinitions: ITabDefinition<TCompProps>[],
  defaultTab: string,
): [ITabDefinition<TCompProps>, (tabId: string) => void, React.ComponentType<TCompProps>] {
  const [selectedTab, setSelectedTab] = React.useState<ITabDefinition<TCompProps>>(() =>
    getTabDef<TCompProps>(defaultTab, tabDefinitions),
  );
  const history = useHistory();
  const params = useParams();
  const location = useLocation();

  const changeTab = React.useCallback(
    (tabId: string) => {
      const newTab = tabDefinitions.find((tab) => tab.tabId === tabId);
      if (newTab) {
        setSelectedTab(newTab);
        history.replace(generatePath(newTab.route, params));
      }
    },
    [history, params, tabDefinitions],
  );

  // Select tab depending on the route
  React.useEffect(() => {
    for (const tab of tabDefinitions) {
      const match = matchPath(location.pathname, { path: tab.route, exact: true });
      if (match !== null) {
        setSelectedTab(tab);
        return;
      }
    }
    // When falling back to default tab, place it in history
    const defaultTabDef = tabDefinitions.find((tabDef) => tabDef.tabId === defaultTab);
    if (defaultTabDef == null) {
      throw new Error(`Default tab ID '${defaultTab}' does not match any provided tab definition`);
    }
    // location.search allows query parameters to the path (e.g. preferRelease in package badges).
    history.replace(generatePath(defaultTabDef.route, params) + location.search);
  }, [setSelectedTab, location, tabDefinitions, defaultTab, history, params]);

  const CurrentTabComponent = React.useMemo(() => {
    const component = tabDefinitions.find((tab) => tab.tabId === selectedTab.tabId)?.component;
    if (component == null) {
      throw new Error("No registered tab with given ID found");
    }
    return component;
  }, [selectedTab, tabDefinitions]);

  return [selectedTab, changeTab, CurrentTabComponent];
}

function getTabDef<TCompProps>(
  tabId: string,
  tabDefinitions: ITabDefinition<TCompProps>[],
): ITabDefinition<TCompProps> {
  const tabDef = tabDefinitions.find((tabDef) => tabDef.tabId === tabId);
  if (tabDef == null) {
    throw new Error(`Tab with id '${tabId}' not found!`);
  } else {
    return tabDef;
  }
}
